import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignupForm } from '@/components/auth/SignupForm';
import { cognitoAuth } from '@/services/cognitoAuth';
import { Header } from '@/components/layout/Header';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  useEffect(() => {
    // Check if user is already authenticated
    if (cognitoAuth.isAuthenticated()) {
      const searchParams = new URLSearchParams(location.search);
      const returnUrl = searchParams.get('returnUrl');
      const returnTo = returnUrl || location.state?.returnTo || '/account/';
      navigate(returnTo, { replace: true });
    }
  }, [navigate, location.state, location.search]);

  const handleAuthSuccess = () => {
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get('returnUrl');
    const returnTo = returnUrl || location.state?.returnTo || '/account/';
    navigate(returnTo, { replace: true });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <Header />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Left Column - Value Proposition */}
          <div>
            <div className="mb-8">
              <div className="inline-block px-3 py-1 bg-blue-600/10 text-blue-600 text-sm font-semibold rounded-full mb-4">
                No Subscription Required
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight">
                {mode === 'login' ? 'Access Your Dashboard' : 'Save Your Work'}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                {mode === 'login'
                  ? 'View search history, download past results, and manage your account.'
                  : 'Track batches, access search history, and download results anytime.'
                }
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">View Complete History</h3>
                  <p className="text-sm text-slate-600">Access all batch jobs and searches with full details</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Re-Download Results</h3>
                  <p className="text-sm text-slate-600">Get your processed files again without reprocessing</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Saved Payment Methods</h3>
                  <p className="text-sm text-slate-600">Speed up checkout with securely stored cards</p>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-blue-500 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">
                Create a free account to track your history and usage. No subscription or contract required—pay as you go for each search or batch.
              </p>
            </div>
          </div>

          {/* Right Column - Auth Form */}
          <div>
            <AnimatePresence mode="wait">
              {mode === 'login' ? (
                <LoginForm
                  key="login"
                  onLoginSuccess={handleAuthSuccess}
                  onSwitchToSignup={() => setMode('signup')}
                />
              ) : (
                <SignupForm
                  key="signup"
                  onSignupSuccess={handleAuthSuccess}
                  onSwitchToLogin={() => setMode('login')}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-200 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Logo & About */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4" aria-label="CollectABILITY Home">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="40"
                />
              </Link>
              <p className="text-slate-700 text-sm mb-4 max-w-md">
                AI-powered debt collection intelligence platform for smarter collections and higher returns.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="mailto:support@collectability.ai"
                  className="text-slate-700 hover:text-slate-900 text-sm transition-colors"
                >
                  support@collectability.ai
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/batch" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Batch Processing
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Web Search
                  </Link>
                </li>
                <li>
                  <a href="/#features" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Features
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/#contact" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/account" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    My Account
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-300 pt-8 space-y-3">
            <FCRAFooterText />
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-slate-700 text-sm text-center md:text-left">
                &copy; 2024 CollectABILITY, LLC. All rights reserved.
              </p>
              <p className="text-slate-700 text-sm text-center md:text-right">
                By {mode === 'login' ? 'signing in' : 'creating an account'}, you agree to our{' '}
                <Link to="/terms" className="text-collectability-blue hover:text-collectability-blue-dark transition-colors">
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-collectability-blue hover:text-collectability-blue-dark transition-colors">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};


